<div class="modal-header et-header" hsDraggable="modal-dialog">
  <div class="et-header-left">
    <!-- The white tile marks the dialog as an editor, and is the one place the primary colour is
         inverted out of the header bar. -->
    <span class="et-header-badge">
      <i class="mdi mdi-pencil"></i>
    </span>
    <div class="et-heading">
      <h4 class="modal-title et-title">{{ vm?.title }}</h4>
      <!-- Provenance: which source, which schema, which account. -->
      <div class="et-crumbs" *ngIf="vm?.breadcrumb as crumb">
        <span class="et-crumb">{{ crumb.source }}</span>
        <span class="et-crumb-sep">·</span>
        <span class="et-crumb">{{ crumb.tranCategory }}</span>
        <ng-container *ngIf="crumb.account as account">
          <span class="et-crumb-sep">·</span>
          <span class="et-crumb-chip">{{ account }}</span>
        </ng-container>
      </div>
      <!-- Secondary note naming the offending record key, mirroring the prototype's failed-state
           note. Rendered for any resolution that has something to name, not only the blocked ones. -->
      <span class="et-detail" *ngIf="vm?.detailLine as detailLine">{{
        detailLine
      }}</span>
    </div>
  </div>
  <span
    class="et-close"
    role="button"
    tabindex="0"
    title="Close without saving"
    [class.et-close-disabled]="saving"
    [attr.aria-disabled]="saving || null"
    (click)="dismiss()"
    (keydown.enter)="dismiss()"
  >
    <i class="mdi mdi-close"></i>
  </span>
</div>

<!-- Summary bar: how the record stands, field by field, before you read a single row.

     Deliberately a SIBLING of the body rather than its first child. It is header, not content: a
     tally you have scrolled past is a tally you cannot act on, and "Hide Absent Sources" is a
     control over the whole list — both have to stay reachable while you read the bottom of it.
     Keeping it outside the scroll box, rather than sticky inside it, also leaves `.et-row-head`
     free to go on sticking to `top: 0` with nothing stacked above it to offset against. -->
<ng-container *ngIf="vm as model">
  <div class="et-summary-bar" *ngIf="!model.blocked" hsDraggable="modal-dialog">
    <span
      class="et-summary-tally"
      *ngFor="let entry of statusSummary"
      [attr.data-status]="entry.status"
    >
      <i class="mdi" [ngClass]="[entry.icon, entry.tone]"></i>
      <span class="et-summary-count">{{ entry.count }}</span>
      <span class="et-summary-label">{{ entry.label }}</span>
    </span>
    <!-- On by default: a record is usually matched against one source, and three mostly-empty
         columns push the values you came to read off the side of the dialog. -->
    <label class="et-hide-absent" *ngIf="model.comparisonSources.length">
      <input
        type="checkbox"
        [checked]="hideAbsentSources"
        (change)="hideAbsentSources = !hideAbsentSources"
      />
      Hide Absent Sources
      <span class="et-hide-absent-count" *ngIf="absentSourceCount"
        >({{ absentSourceCount }})</span
      >
    </label>
  </div>
</ng-container>

<!-- `(scroll)` re-anchors the open value panel. The panel is on `body`, outside this box, so a scroll
     moves the cell out from under it. It re-measures rather than closing, because focusing a
     partly-visible cell makes the browser scroll it into view, and closing on that would dismiss the
     panel the same click had just opened. -->
<div
  class="modal-body et-body"
  [class.nf-loader-parent-element-restrict]="frozen"
  [attr.inert]="frozen ? '' : null"
  [attr.aria-busy]="busy || saving || null"
  hsDraggable="modal-dialog"
  (scroll)="picker.reanchor()"
>
  <!-- PVAL-26492: reading the trade, or stopped on an error. PVAL-26451: also up, as "Loading
       Matches…", while the server-matched counterparts are read — the dialog opens without waiting.

       The app's own loader, used the way the Create/Edit Transaction modal uses it: `active` turns
       `pointer-events` on so the overlay BLOCKS the fields behind it, and an `errorLabel` swaps the
       spinner for an error icon and message. Setting `errorLabel` and leaving `active` on is what
       freezes the dialog — a read that failed must not reveal a half-filled form the user could
       edit and try to save. The detail goes in `hs-dialog-error-details` below the body.

       The overlay is `position: absolute; inset: 0` INSIDE this scrolling box, so it is only as tall
       as the box's visible height and scrolls away with the content: on its own it covered the first
       screenful and left everything below it scrollable and editable. While frozen the body therefore
       carries the loader's own `nf-loader-parent-element-restrict` (clips and stops the scrolling, so
       the overlay always covers what can be seen) and `inert` (nothing in it takes focus, so Tab
       cannot reach a field the overlay is not over). -->
  <!-- PVAL-26440: the same overlay covers the fields while a save is in flight. -->
  <hs-loader
    class="{{ frozen ? 'active' : '' }}"
    [loading]="busy || saving"
    [label]="saving ? savingLabel : loadingLabel"
    [errorLabel]="errorLabel"
    [showText]="frozen"
  ></hs-loader>

  <ng-container *ngIf="vm as model">
    <ng-container *ngIf="!model.blocked">
      <div class="et-fields">
        <!-- Every row spans the dialog and packs fixed-width cells to the left, so the banding and
             the rules run edge to edge while the columns stay a constant width. The widths are
             fixed, so rows align with each other without needing shared grid tracks. -->
        <div class="et-row et-row-head">
          <div class="et-col-head et-col-head-status"></div>
          <div class="et-col-head et-col-head-label">Field</div>
          <div class="et-col-head et-col-head-value">
            <i class="mdi mdi-pencil"></i> {{ valueColumnLabel }}
          </div>
          <div
            class="et-col-head et-col-head-comparison"
            *ngFor="let source of visibleSources"
            [class.et-col-head-absent]="source.absent"
          >
            {{ source.label }}
          </div>
        </div>

        <ng-container *ngFor="let row of rows">
          <!-- A section heading is its own full-width band. -->
          <div class="et-section-row" *ngIf="row.kind === 'section'">
            {{ row.name }}
          </div>

          <ng-container *ngIf="row.kind === 'field'">
            <div class="et-row" *ngIf="row.field as field">
              <div class="et-status" [title]="statusMeta(field).label">
                <i
                  class="mdi"
                  [ngClass]="[statusMeta(field).icon, statusMeta(field).tone]"
                ></i>
              </div>

              <label class="et-label" [attr.for]="field.id">
                {{ field.label }}
                <!-- One marker, two states: the asterisk that says "required" also says "not
                     answered yet" when it is, rather than a second glyph appearing beside it. -->
                <span
                  class="et-required"
                  *ngIf="field.required"
                  [class.et-required-unmet]="requiredUnanswered(field)"
                  [attr.title]="
                    requiredUnanswered(field)
                      ? 'Required — not yet complete'
                      : 'Required'
                  "
                  aria-hidden="true"
                  >*</span
                >
              </label>

              <!-- A collection's summary: its grouped value, and the control that opens the lines
                   behind it. It is an ordinary row in its own group, not a block appended to the
                   dialog. -->
              <div
                class="et-value et-value-summary"
                *ngIf="collectionFor(field) as collection; else plainValue"
              >
                <span class="et-value-text">{{ field.value }}</span>
                <i
                  class="mdi mdi-table et-array-open"
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="'Edit ' + collection.label"
                  [title]="'Edit ' + collection.label"
                  (click)="openCollection(collection)"
                  (keydown.enter)="openCollection(collection)"
                ></i>
              </div>

              <!-- `#cell` is the box an enum's panel hangs off: the design anchors to the CELL, not
                   to the control inside it, so the panel takes the column's width and its border
                   lands on the cell's own rule. -->
              <ng-template #plainValue>
                <div
                  class="et-value"
                  [class.et-value-readonly]="!field.editable"
                  #cell
                >
                  <!-- Read-only fields render as text, marked with the lock the design uses: nothing
                     should look like it invites typing, and "cannot edit" reads as deliberate. -->
                  <span
                    class="et-value-text et-value-locked"
                    *ngIf="!field.editable"
                  >
                    {{ field.value }}
                    <i class="mdi mdi-lock et-lock" title="Read-only"></i>
                  </span>

                  <!-- PVAL-26492: an editable field is EDITABLE. A list whose resolve endpoint is
                       not wired yet used to render here as a disabled box, which locked a field the
                       document says `edit: true`. It now renders its real control; a `catalog` or
                       `search` enum simply opens an empty picker until its values arrive. -->
                  <ng-container
                    *ngIf="field.editable"
                    [ngSwitch]="control(field)"
                  >
                    <!-- A resolved closed set: the design's own anchored panel, opened from the
                         cell. NOT a native `<select>` — the panel draws its rows, its rules, its
                         selected tint and its five-row cap itself, and the operating system owns
                         every one of those in a dropdown. The trigger is therefore an ordinary
                         element wearing `.et-input`, so it sits on the same baseline as the boxes
                         around it, and it shows the LABEL while the panel writes the VALUE — which
                         is what lets an id-backed list be authored without touching this again. -->
                    <span class="et-input-wrap" *ngSwitchCase="'enum'">
                      <span
                        class="et-input et-pop-trigger vp-trigger"
                        role="button"
                        tabindex="0"
                        aria-haspopup="listbox"
                        [id]="field.id"
                        [attr.aria-expanded]="picker.isOpen(field.field)"
                        [attr.title]="pickerText(field) || null"
                        (mousedown)="openPicker(field, cell, $event)"
                        (keydown.enter)="openPicker(field, cell, $event)"
                        (keydown.space)="openPicker(field, cell, $event)"
                        (keydown.escape)="picker.close()"
                        >{{ pickerText(field) }}</span
                      >
                      <!-- The chevron reports the panel's state, so it points at where the list
                           currently is. -->
                      <i
                        class="mdi et-input-icon"
                        [ngClass]="
                          picker.isOpen(field.field)
                            ? 'mdi-chevron-up'
                            : 'mdi-chevron-down'
                        "
                      ></i>
                    </span>

                    <!-- An unbounded reference set: a text box that asks the resolve endpoint what the
                         typed text matches and offers the answers in the same anchored panel an enum
                         uses. Still a text box, deliberately — the set has no end, so there is
                         nothing to show before something is typed, and a field whose search fails
                         stays usable rather than becoming a trigger with nothing behind it.

                         `lookupText` rather than `field.value`: a lookup writes an id and reads a
                         name. A `lookup` field with no `search` list is not searchable at all and
                         behaves exactly as it did — ordinary free text with a magnifier. -->
                    <span class="et-input-wrap" *ngSwitchCase="'lookup'">
                      <input
                        #lookup
                        class="et-input vp-trigger"
                        type="text"
                        autocomplete="off"
                        [id]="field.id"
                        [value]="lookupText(field)"
                        [attr.placeholder]="
                          searchable(field)
                            ? 'Type ' +
                              field.minChars +
                              '+ characters to search'
                            : null
                        "
                        (input)="onLookupInput(field, lookup.value, cell)"
                        (focus)="onArm(field)"
                        (blur)="onCommit(field)"
                      />
                      <!-- The glyph reports the search rather than merely decorating the box: a
                           request in flight, nothing found, or neither. -->
                      <i
                        class="mdi et-input-icon"
                        [ngClass]="
                          searching === field
                            ? 'mdi-loading et-input-spin'
                            : noMatches === field
                            ? 'mdi-magnify-close et-input-empty'
                            : 'mdi-magnify'
                        "
                        [attr.title]="noMatches === field ? 'No matches' : null"
                      ></i>
                    </span>

                    <!-- The app's own date control, not a native `<input type="date">`.

                         It was a native input whose `type` SWAPPED on focus — text at rest so the
                         value read `09/03/2026` like the column beside it, `date` once armed so a
                         calendar existed at all — with a glyph calling the gesture-gated
                         `showPicker()`. The browser drew its OWN calendar button next to ours,
                         which is the two icons; and a native date input renders in the BROWSER's
                         locale, not the app's.

                         `nf-datepicker` is the control every other helios dialog uses, it reaches
                         here through the `NfFormsModule` this module already imports, and it
                         displays `MM/dd/yyyy` over an ISO value — which is `dateUS` exactly.

                         Bound to a reactive control, like every other use of it in the repo. It
                         has no value input: its inner input binds `[formControl]` to this very
                         control, so the control is the whole channel. No `(focus)`/`(blur)` pair
                         either — it renders its own text, so there is no edit form to swap in. -->
                    <nf-datepicker
                      *ngSwitchCase="'date'"
                      class="et-datepicker"
                      [id]="field.id"
                      [formControl]="dateControl(field)"
                      [format]="dateDisplayFormat"
                      [placeholder]="dateDisplayFormat"
                    ></nf-datepicker>

                    <!-- Text, not `type="number"`: a native number input cannot show thousands
                         separators, so an amount would be grouped in the comparison column and
                         ungrouped beside it. -->

                    <input
                      #num
                      *ngSwitchCase="'number'"
                      class="et-input et-input-number"
                      type="text"
                      inputmode="decimal"
                      [id]="field.id"
                      [value]="field.value"
                      (input)="onEdit(field, num.value)"
                      (focus)="onArm(field)"
                      (blur)="onCommit(field)"
                    />

                    <input
                      #line
                      *ngSwitchDefault
                      class="et-input"
                      type="text"
                      [id]="field.id"
                      [value]="field.value"
                      (input)="onEdit(field, line.value)"
                      (focus)="onArm(field)"
                      (blur)="onCommit(field)"
                    />
                  </ng-container>

                  <!-- PVAL-26685: a derivation in flight, or one that failed. A failure leaves the
                       value as it was and offers to ask again for just this field. -->
                  <span
                    class="et-derivation"
                    *ngIf="field.derivationState === 'pending'"
                    title="Deriving…"
                  >
                    <i class="mdi mdi-loading et-derivation-spin"></i>
                  </span>
                  <span
                    class="et-derivation et-derivation-failed"
                    *ngIf="field.derivationState === 'failed'"
                  >
                    <i
                      class="mdi mdi-alert-circle-outline"
                      title="This value could not be derived"
                    ></i>
                    <button
                      type="button"
                      class="et-derivation-retry"
                      (click)="retryDerivation(field)"
                    >
                      Retry
                    </button>
                  </span>

                  <span
                    class="et-required-hint"
                    *ngIf="field.requiredHint as hint"
                    [class.et-required-hint-unmet]="requiredRuleBroken(field)"
                    >{{ hint }}</span
                  >
                </div>
              </ng-template>

              <div
                class="et-comparison"
                *ngFor="let source of visibleSources"
                [class.et-comparison-diff]="
                  field.status === 'diff' && source.origin === 'cust'
                "
              >
                {{ field.comparisons[source.origin] }}
              </div>
            </div>
          </ng-container>
        </ng-container>
      </div>

      <!-- Footer container: a value too long for a grid cell, laid out full width beneath it. Notes
           lives here, and it is the field most likely to be holding Save — so it carries the same
           required marker (in the same two states) and the same word-count hint the grid rows do,
           never a quieter version of them. -->
      <section class="et-footer-fields" *ngIf="model.footer.length">
        <div class="et-footer-field" *ngFor="let field of model.footer">
          <label class="et-field-label" [attr.for]="field.id">
            {{ field.label }}
            <span
              class="et-required"
              *ngIf="field.required"
              [class.et-required-unmet]="requiredUnanswered(field)"
              [attr.title]="
                requiredUnanswered(field)
                  ? 'Required — not yet complete'
                  : 'Required'
              "
              aria-hidden="true"
              >*</span
            >
          </label>
          <textarea
            #memo
            class="et-input et-textarea"
            *ngIf="field.editable; else footerRead"
            [id]="field.id"
            rows="3"
            [value]="field.value"
            (input)="onEdit(field, memo.value)"
          ></textarea>
          <ng-template #footerRead>
            <span class="et-field-value">{{ field.value }}</span>
          </ng-template>
          <span
            class="et-required-hint"
            *ngIf="field.requiredHint as hint"
            [class.et-required-hint-unmet]="requiredRuleBroken(field)"
            >{{ hint }}</span
          >
        </div>
      </section>
    </ng-container>
  </ng-container>
</div>

<!-- PVAL-26492: OUTSIDE `.et-body`, between it and the footer.

     Inside the body it sat after the last field and scrolled out of reach — on a dialog with this
     many rows the error was below the fold, so a failed read looked like nothing had happened. Here
     it is pinned between the scrolling fields and the footer and is visible the moment it appears,
     without taking space while there is no error: the component renders nothing until
     `errorDetails` is set. -->
<hs-dialog-error-details
  [errorHeader]="errorHeader"
  [errorDetails]="errorDetails"
></hs-dialog-error-details>

<div class="modal-footer et-footer" hsDraggable="modal-dialog">
  <!-- One depth, both halves of Req. "Req / Req Parameter — required to save": a blank required
       field and a value that breaks the minimum word count the row is displaying are both counted
       here and both disable Save, including a blank required cell on a collection's lines. Whether a
       value that IS there is legal is the save endpoint's question and is not asked. -->
  <span class="et-missing" *ngIf="missingRequiredCount as missing">
    <i class="mdi mdi-alert-circle"></i>
    {{ missing }} required field{{ missing === 1 ? '' : 's' }} incomplete
  </span>
  <!-- PVAL-26440: why the last Save did not go through, or why nothing was sent. -->
  <span class="et-save-error" role="alert" *ngIf="saveError">
    <i class="mdi mdi-alert-octagon"></i>
    {{ saveError }}
  </span>
  <div class="et-footer-actions">
    <button nf-button="cancel" [disabled]="saving" (click)="dismiss()"></button>
    <button
      nf-button="primary"
      class="et-save"
      *ngIf="vm && !vm.blocked"
      [disabled]="saveDisabled"
      [attr.title]="saveTitle"
      (click)="save()"
    >
      Save
    </button>
  </div>
</div>

<!-- The open value panel, rendered ONCE and outside the row loop: it is positioned against the
     viewport, not against the row, and only one can be open at a time. It moves itself onto `body`,
     so the dialog's edges cannot clip it. -->
<hs-value-picker
  *ngIf="picker.open"
  [rows]="picker.rows"
  [columns]="picker.columns"
  [style.left.px]="picker.left"
  [style.top.px]="picker.top"
  [style.width.px]="picker.width"
  (picked)="pickValue($event)"
></hs-value-picker>
